import { clsx } from 'clsx';
import { useSelect } from 'downshift';
import React, { useMemo, useState } from 'react';
import { Input } from 'ui';
import { SelectorIcon } from '~/components/Icons/SelectorIcon';
import useHasFeature from '~/hooks/useHasFeature';
import SearchIcon from '~/public/static/svg/SearchIcon';
import { RailsDetail } from '~/typings/services/rails/item';

interface Props {
  clearable?: boolean;

  // Parent detail. Used for rendering the label and passing through "detail" in onChange
  detail?: RailsDetail;

  error?: string;

  id?: string;

  /**
   * Fired when selection changes
   * @param detail Same `detail` from the props
   * @param selection Selected detail
   */
  onChange: (
    detail: RailsDetail,
    selection: RailsDetail | 'variations' | null,
  ) => void;

  searchable?: boolean;

  // Pass the full detail object
  value?: RailsDetail | 'variations' | null;

  variationsEnabled?: boolean;
}

const DetailSelector: React.FC<Props> = ({
  clearable,
  detail,
  error,
  id,
  onChange,
  searchable,
  value,
  variationsEnabled,
}) => {
  const hasVariations = useHasFeature('list_variations');
  const [inputValue, setInputValue] = useState('');

  const items = useMemo<(RailsDetail | 'variations')[]>(() => {
    let children = detail?.children ?? [];

    if (searchable && inputValue.length > 0) {
      const other = children.find(child => child.name === 'Other');
      const searchString = inputValue.toLowerCase();

      children = children.filter(
        child =>
          child.name !== 'Other' &&
          child.name.toLowerCase().includes(searchString),
      );

      if (other) {
        children.push(other);
      }
    }

    if (
      variationsEnabled &&
      hasVariations &&
      detail?.children &&
      detail?.has_variations
    ) {
      return [...children, 'variations'];
    } else {
      return children;
    }
  }, [
    detail?.children,
    detail?.has_variations,
    searchable,
    inputValue,
    variationsEnabled,
    hasVariations,
  ]);

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    setHighlightedIndex,
    reset,
  } = useSelect({
    items,
    id: id || (detail ? `detail-${detail.slug}` : undefined),

    itemToString: item =>
      item === 'variations' ? 'variations' : item?.name || '',
    onSelectedItemChange: changedDetail =>
      onChange(detail!, changedDetail.selectedItem || null),
    stateReducer: (state, changes) => {
      // Ignore space button
      if (
        changes.type === useSelect.stateChangeTypes.MenuKeyDownSpaceButton ||
        changes.key === 'Space'
      ) {
        setInputValue(p => p + ' ');
        return state;
      }
      return changes.changes;
    },
  });

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputValue(e.target.value);
    setHighlightedIndex(0);
  }

  function handleReset() {
    reset();
    if (detail) {
      onChange(detail, null);
    }
    setInputValue('');
  }

  const lowerCaseName = detail?.name.toLowerCase();

  return (
    <div className="relative">
      <div
        {...getToggleButtonProps()}
        className={clsx(
          'flex w-full cursor-pointer items-center focus-within:border-green-500 focus-within:ring',
          isOpen ? 'rounded-t-md border' : 'rounded-md border',
          !!error && 'border-red-500',
        )}
        style={
          isOpen
            ? {
                borderBottomColor: '#fff',
              }
            : undefined
        }
      >
        <button
          className={clsx(
            'flex-1 rounded px-4 py-2 text-left outline-none',
            value ? 'font-semibold' : '',
          )}
          type="button"
        >
          {value === 'variations'
            ? 'I have multiple'
            : value?.name || (
                <span className="text-grey-700">
                  Select {lowerCaseName ?? 'value'}
                </span>
              )}
        </button>

        {!!value && clearable && (
          <button
            type="button"
            className="p-2"
            onClick={() => {
              handleReset();
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        <button type="button" tabIndex={-1} className="py-2.5 pl-1 pr-2">
          <SelectorIcon />
        </button>
      </div>

      <ul
        {...getMenuProps()}
        className="absolute top-full z-20 w-full overflow-y-scroll rounded-b border-b border-l border-r bg-white shadow-lg"
        style={{
          visibility: isOpen ? 'visible' : 'collapse',
          maxHeight: 400,
        }}
      >
        {isOpen && (
          <>
            {searchable && (
              <Input
                leftAdornment={
                  <SearchIcon
                    className="text-slate-green-500 ml-2"
                    fill="currentColor"
                  />
                }
                onFocus={e => e.target.select()}
                type="search"
                inputClassName="!py-4 rounded-sm"
                className="mx-4 mb-4 mt-2 rounded-sm"
                placeholder={`Search ${lowerCaseName ?? 'detail'}...`}
                onChange={handleInputChange}
                value={inputValue}
                autoCapitalize="off"
                autoComplete="off"
              />
            )}

            {items.map((child, i) => (
              <li
                key={child === 'variations' ? 'variations' : child.id}
                {...getItemProps({
                  item: child,
                  index: i,
                })}
                className={clsx(
                  'px-4 py-2 md:py-1',
                  highlightedIndex === i && 'bg-pastel-green-500',
                  value !== 'variations' &&
                    child !== 'variations' &&
                    value?.id === child.id &&
                    'font-semibold',
                  value === 'variations' &&
                    child === 'variations' &&
                    'font-semibold',
                  child === 'variations' && 'border-t',
                )}
              >
                {child === 'variations' ? 'I have multiple' : child.name}
              </li>
            ))}
          </>
        )}
      </ul>
    </div>
  );
};

export default DetailSelector;
